import { useParams, useNavigate } from 'react-router-dom';
import { useProjects } from '@/context/ProjectContext';
import { useAuth } from '@/context/AuthContext';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/StatusBadge';
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Users,
  Building2,
  Edit,
  CheckCircle,
  MessageSquare,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getPaymentStatus, getPaymentTypeLabel } from '@/utils/helpers';

export const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { projects, updateProject, updatePaymentStatus, addComment } = useProjects();
  const { user, isAdmin } = useAuth();

  const project = projects.find((p) => p.id === id);

  const [isEditing, setIsEditing] = useState(false);

  // Project form state
  const [projectName, setProjectName] = useState('');
  const [clientName, setClientName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [outsourcingPartner, setOutsourcingPartner] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [commentText, setCommentText] = useState('');

  // Payment editing state
  const [editedPayments, setEditedPayments] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentInvoiceNumber, setPaymentInvoiceNumber] = useState('');
  const [highlightedPaymentId, setHighlightedPaymentId] = useState(null);

  useEffect(() => {
    if (project) {
      setProjectName(project.projectName);
      setClientName(project.clientName);
      setInvoiceNumber(project.invoiceNumber || '');
      setOutsourcingPartner(project.outsourcingPartner || '');
      setPaymentType(project.paymentType || '');
      setEditedPayments(project.paymentSchedule.map(p => ({ ...p }))); // make a copy
      
      // Check if we need to highlight a specific payment (from URL hash)
      const hash = window.location.hash.replace('#payment-', '');
      if (hash) {
        setHighlightedPaymentId(hash);
        // Scroll to the payment after a short delay
        setTimeout(() => {
          const element = document.getElementById(`payment-${hash}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Remove highlight after animation
            setTimeout(() => setHighlightedPaymentId(null), 3000);
          }
        }, 100);
      }
    }
  }, [project]);

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <p className="text-lg text-muted-foreground">Project not found</p>
        <Button onClick={() => navigate('/projects')} className="mt-4">
          Back to Projects
        </Button>
      </div>
    );
  }

  const totalAmount = editedPayments.reduce((sum, p) => sum + Number(p.amount), 0);
  const paidAmount = editedPayments.filter(p => p.status === 'paid').reduce((sum, p) => sum + Number(p.amount), 0);
  const pendingAmount = totalAmount - paidAmount;

  // Handlers
  const handleSaveProject = () => {
    updateProject(id, {
      projectName,
      clientName,
      invoiceNumber,
      outsourcingPartner,
      paymentType,
      paymentSchedule: editedPayments,
    });
    toast.success('Project updated successfully');
    setIsEditing(false);
  };

  const handlePaymentChange = (paymentId, field, value) => {
    setEditedPayments(prev =>
      prev.map(p => (p.id === paymentId ? { ...p, [field]: value } : p))
    );
  };

  const handleMarkAsPaid = () => {
    if (!selectedPayment) return;
    updatePaymentStatus(project.id, selectedPayment.id, 'paid', paymentNotes, paymentInvoiceNumber);
    toast.success('Payment marked as paid');
    setSelectedPayment(null);
    setPaymentNotes('');
    setPaymentInvoiceNumber('');
  };

  const handleAddComment = () => {
    if (!commentText.trim()) return;
    addComment(project.id, {
      commentBy: isAdmin ? 'Admin' : user?.name,
      text: commentText,
    });
    toast.success('Comment added successfully');
    setCommentText('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button variant="ghost" className="gap-2 mb-4" onClick={() => navigate('/projects')}>
          <ArrowLeft className="h-4 w-4" />
          Back to Projects
        </Button>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="space-y-2">
            {isEditing ? (
              <>
                <Input
                  value={projectName}
                  onChange={e => setProjectName(e.target.value)}
                  placeholder="Project Name"
                  className="text-3xl font-bold"
                />
                <Input
                  value={clientName}
                  onChange={e => setClientName(e.target.value)}
                  placeholder="Client Name"
                />
                <Input
                  value={invoiceNumber}
                  onChange={e => setInvoiceNumber(e.target.value)}
                  placeholder="Invoice Number"
                />
              </>
            ) : (
              <>
                <h1 className="text-3xl font-bold">{project.projectName}</h1>
                <p className="text-muted-foreground mt-1">{project.clientName}</p>
                {project.invoiceNumber && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Invoice: {project.invoiceNumber}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex gap-2">
            <Badge variant="outline" className="capitalize">
              {getPaymentTypeLabel(paymentType)}
            </Badge>
            {!isEditing && (
              <Button variant="outline" size="sm" className="gap-2" onClick={() => setIsEditing(true)}>
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        </div>

        {isEditing && (
          <div className="mt-4 flex gap-2">
            <Button onClick={handleSaveProject}>Save</Button>
            <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalAmount)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Received</CardTitle>
            <CheckCircle className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{formatCurrency(paidAmount)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {((paidAmount / totalAmount) * 100).toFixed(1)}% completed
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(pendingAmount)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {editedPayments.filter(p => p.status !== 'paid').length} payments remaining
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Payment Schedule */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Payment Schedule</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {editedPayments.map(payment => {
                const status = getPaymentStatus(payment.dueDate, payment.status);
                return (
                  <div
                    key={payment.id}
                    id={`payment-${payment.id}`}
                    className={`p-4 rounded-lg border transition-all ${
                      status === 'overdue' ? 'border-destructive/50 bg-destructive/5' : 'bg-card'
                    } ${highlightedPaymentId === payment.id ? 'ring-2 ring-primary ring-offset-2 animate-pulse' : ''}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        {isEditing ? (
                          <>
                            <Input
                              value={payment.paymentName}
                              onChange={e => handlePaymentChange(payment.id, 'paymentName', e.target.value)}
                            />
                            <Input
                              type="number"
                              value={payment.amount}
                              onChange={e => handlePaymentChange(payment.id, 'amount', Number(e.target.value))}
                            />
                            <Input
                              type="date"
                              value={payment.dueDate}
                              onChange={e => handlePaymentChange(payment.id, 'dueDate', e.target.value)}
                            />
                            <Textarea
                              value={payment.notes || ''}
                              onChange={e => handlePaymentChange(payment.id, 'notes', e.target.value)}
                              rows={2}
                            />
                            <Input
                              placeholder="Invoice Number"
                              value={payment.invoiceNumber || ''}
                              onChange={e => handlePaymentChange(payment.id, 'invoiceNumber', e.target.value)}
                            />
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium">{payment.paymentName}</p>
                              <StatusBadge status={status} />
                            </div>
                            <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                              <span>Due: {formatDate(payment.dueDate)}</span>
                              {payment.invoiceNumber && (
                                <span>Invoice: {payment.invoiceNumber}</span>
                              )}
                              {payment.paidDate && (
                                <span className="text-success">Paid: {formatDate(payment.paidDate)}</span>
                              )}
                            </div>
                            {payment.notes && (
                              <p className="text-sm text-muted-foreground mt-2">Note: {payment.notes}</p>
                            )}
                          </>
                        )}
                      </div>

                      {!isEditing && status !== 'paid' && (
                        <div className="text-right">
                          <p className="font-bold text-lg">{formatCurrency(payment.amount)}</p>
                          <Button size="sm" className="mt-2" onClick={() => {
                            setSelectedPayment(payment);
                            setPaymentInvoiceNumber(payment.invoiceNumber || '');
                          }}>
                            Mark as Paid
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Project Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Project Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Client</p>
                  <p className="text-sm text-muted-foreground">{clientName}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Invoice Number</p>
                  <p className="text-sm text-muted-foreground">{invoiceNumber || '—'}</p>
                </div>
              </div>
              {outsourcingPartner && (
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Outsourcing Partner</p>
                    <p className="text-sm text-muted-foreground">{outsourcingPartner}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Created</p>
                  <p className="text-sm text-muted-foreground">{formatDate(project.createdAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comments */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Comments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {project.comments && project.comments.length > 0 && (
                <div className="space-y-3 max-h-64 overflow-y-auto custom-scrollbar">
                  {project.comments.map((comment) => (
                    <div key={comment.id} className="p-3 rounded-lg bg-muted">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium">{comment.commentBy}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(comment.date)}</p>
                      </div>
                      <p className="text-sm">{comment.text}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="comment">Add Comment</Label>
                <Textarea
                  id="comment"
                  placeholder="Write a comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  rows={3}
                />
                <Button onClick={handleAddComment} size="sm" className="w-full">
                  Add Comment
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mark as Paid Modal */}
      {selectedPayment && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white p-6 rounded-lg w-96 space-y-4">
            <h2 className="text-lg font-bold">Mark Payment as Paid</h2>
            <p>{selectedPayment.paymentName}</p>
            <p className="text-2xl font-bold">{formatCurrency(selectedPayment.amount)}</p>
            <p className="text-sm text-muted-foreground">Due: {formatDate(selectedPayment.dueDate)}</p>
            <Label htmlFor="paymentInvoice">Invoice Number (Optional)</Label>
            <Input
              id="paymentInvoice"
              value={paymentInvoiceNumber}
              onChange={(e) => setPaymentInvoiceNumber(e.target.value)}
              placeholder="e.g., INV-2025-001"
            />
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              rows={3}
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSelectedPayment(null)}>
                Cancel
              </Button>
              <Button onClick={handleMarkAsPaid}>Confirm</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
